/**
 * What is wrong with a day's schedule (M1-12), as pure functions.
 *
 * <b>Warnings, never blocks.</b> Every rule here describes something a
 * dispatcher may well have done on purpose — double-booking a technician for
 * two jobs in the same building, or sending someone out before opening for an
 * emergency. The AC is explicit about it, so nothing in this module or its
 * callers refuses an action; they only say what is true. The API agrees: the
 * schedule endpoint has no overlap check to fail.
 *
 * Detection is client-side because the board already holds the whole window it
 * is drawing. Asking the server would mean a round trip to describe data that
 * is already on the screen, and it would still have to be recomputed
 * optimistically the moment anything moved.
 */
import {
  SLOT_MINUTES,
  businessWindow,
  hoursForDate,
  jobSpan,
  type BoardDay,
  type DayHours,
  type SchedulableJob,
} from './dispatchBoard';
import { availabilityOn, isUnavailable, type AvailabilityMap } from './availability';

export type ConflictKind =
  | 'overlap'
  | 'outsideHours'
  | 'closedDay'
  | 'unavailable'
  | 'vehicleInMaintenance';

export interface JobConflict {
  kind: ConflictKind;
  /** For `overlap`: the other jobs the technician is booked for at the time. */
  withJobIds: string[];
}

/** Conflicts by job id. A job absent from the map has none. */
export type ConflictMap = Map<string, JobConflict[]>;

/**
 * Every conflict across the days a board is showing.
 *
 * Days are passed in rather than derived so this serves both views unchanged:
 * the day view hands it one day, the week view seven. A job that runs across
 * midnight is examined on each day it touches, which is also how it is drawn.
 */
export function detectConflicts(
  jobs: readonly SchedulableJob[],
  days: readonly BoardDay[],
  businessHours: readonly DayHours[],
  /**
   * Who is working when (M1-39), and what they are driving (M1-27). Optional,
   * and its absence means "say nothing" rather than "everybody is unavailable"
   * — the board renders before this request lands, and a warning that appears
   * because data is missing is worse than no warning at all.
   *
   * One parameter serves both rules because both facts arrive on the same
   * per-day cell of the same single windowed request. A second source here
   * would have meant a second request per board, which is the N+1
   * `GET /api/v1/dispatch/technician-days` exists to avoid.
   */
  availability?: AvailabilityMap,
): ConflictMap {
  const conflicts: ConflictMap = new Map();

  // At most one conflict of each KIND per job, with the related job ids
  // merged. A job that runs across midnight is examined on both days it
  // touches, so without this it collects the same complaint twice and the
  // badge would read "double-booked, double-booked".
  const add = (jobId: string, conflict: JobConflict) => {
    const existing = conflicts.get(jobId);
    if (!existing) {
      conflicts.set(jobId, [conflict]);
      return;
    }
    const sameKind = existing.find((candidate) => candidate.kind === conflict.kind);
    if (!sameKind) {
      existing.push(conflict);
      return;
    }
    for (const id of conflict.withJobIds) {
      if (!sameKind.withJobIds.includes(id)) {
        sameKind.withJobIds.push(id);
      }
    }
  };

  for (const day of days) {
    const hours = hoursForDate(businessHours, day.date);
    const window = businessWindow(hours, day);

    const onThisDay = jobs.filter((job) => intervalOn(job, day) !== null);

    // --- Outside the business's hours -----------------------------------
    for (const job of onThisDay) {
      const span = intervalOn(job, day)!;
      if (window === null) {
        // No hours recorded for this weekday at all: everything on it is
        // out of hours, and saying so once per job is the honest answer.
        add(job.id, { kind: 'closedDay', withJobIds: [] });
        continue;
      }
      if (span.startMinute < window.openMinute || span.endMinute > window.closeMinute) {
        add(job.id, { kind: 'outsideHours', withJobIds: [] });
      }
    }

    // --- What is true about the PERSON this job is assigned to -----------
    //
    // Two rules, one pass, because both read the same per-day cell of the same
    // single windowed request. Neither blocks anything: M1-12's rule is that
    // dispatch stays in charge, and both of these describe something a
    // dispatcher may well be doing on purpose.
    //
    //  * `unavailable` (M1-39) — scheduled onto somebody who is not working.
    //    Calling somebody in on their day off is a normal Tuesday.
    //  * `vehicleInMaintenance` (M1-27) — the AC's "setting a vehicle to
    //    in_maintenance warns dispatch when its primary tech is scheduled that
    //    day". ⚠️ Note where that sentence lives: it is a fact about a VEHICLE
    //    producing a warning on a JOB, so it crosses domains and had to be
    //    given an owner. It is split, and the split is the design — the
    //    VEHICLE half (is this person the primary driver, on this date, of
    //    something in the shop) is `VehicleAssignmentCalendar`'s, server-side,
    //    and arrives pre-resolved as `vehicleMaintenanceWarning`; the JOB half
    //    (is anybody actually scheduled) is this module's, because the board
    //    already holds every job in the window it is drawing. Keeping the
    //    predicate server-side is what stops the board and the tooltip
    //    disagreeing about the same van; computing the job half here is what
    //    avoids a round trip to describe data already on the screen, which
    //    would still have to be recomputed the moment anything moved.
    //
    // Both are the SECOND and FIRST extensions of this module respectively,
    // and both reuse the mechanism rather than sitting beside it: they land in
    // the existing `ConflictBadge`, the existing card `aria-label` and the
    // existing header count, with no new plumbing and no second thing for a
    // dispatcher to learn.
    if (availability) {
      for (const job of onThisDay) {
        if (!job.assignedUserId) {
          continue;
        }
        const state = availabilityOn(availability, job.assignedUserId, day.date);
        // `Unknown` and a missing entry both mean "nobody has told us", which
        // is not a reason to warn anybody about anything. See
        // `lib/availability.ts` for why that distinction carries the story.
        if (isUnavailable(state)) {
          add(job.id, { kind: 'unavailable', withJobIds: [] });
        }
        // Absent data says nothing here either, and for the same reason: a
        // warning that appears because a request failed is worse than none.
        if (state?.vehicleMaintenanceWarning) {
          add(job.id, { kind: 'vehicleInMaintenance', withJobIds: [] });
        }
      }
    }

    // --- Two jobs, one technician, one moment ---------------------------
    for (const [, theirs] of groupByTechnician(onThisDay)) {
      for (const [jobId, others] of overlappingPairs(theirs, day)) {
        add(jobId, { kind: 'overlap', withJobIds: others });
      }
    }
  }

  return conflicts;
}

/**
 * A job's interval within one day, or null if it does not run that day.
 *
 * A job with a start and no end occupies one slot — the same assumption the
 * board draws it with, so a card that looks like it overlaps is one that does.
 */
function intervalOn(
  job: SchedulableJob,
  day: BoardDay,
): { startMinute: number; endMinute: number } | null {
  if (!job.scheduledStart) {
    return null;
  }
  const start = new Date(job.scheduledStart).getTime();
  if (Number.isNaN(start)) {
    return null;
  }
  const rawEnd = job.scheduledEnd ? new Date(job.scheduledEnd).getTime() : Number.NaN;
  const end = Number.isNaN(rawEnd) ? start + SLOT_MINUTES * 60_000 : rawEnd;
  // Runs entirely outside this day.
  if (start >= day.dayEnd.getTime() || end <= day.dayStart.getTime()) {
    return null;
  }
  return jobSpan(job, day.dayStart, day.dayEnd);
}

/** Unassigned jobs are deliberately absent: nobody can be double-booked by them. */
function groupByTechnician(jobs: readonly SchedulableJob[]): Map<string, SchedulableJob[]> {
  const groups = new Map<string, SchedulableJob[]>();
  for (const job of jobs) {
    if (!job.assignedUserId) {
      continue;
    }
    const existing = groups.get(job.assignedUserId);
    if (existing) {
      existing.push(job);
    } else {
      groups.set(job.assignedUserId, [job]);
    }
  }
  return groups;
}

/**
 * Which of one technician's jobs overlap which, by a sweep over their starts.
 *
 * Sorted by start, then each job is compared only against those still running
 * when it begins — an active set that is emptied as jobs end. The naive
 * all-pairs comparison would also work at a technician's daily volume, but the
 * sweep states the actual rule ("still running when this one starts") rather
 * than testing every combination and hoping the predicate is right.
 *
 * Intervals are half-open: a job ending at 10:00 and one starting at 10:00 are
 * back to back, not a double-booking. Flagging those would put a warning on
 * most of a well-packed day and train dispatchers to ignore the badge.
 */
function overlappingPairs(
  jobs: readonly SchedulableJob[],
  day: BoardDay,
): Map<string, string[]> {
  const spans = jobs
    .map((job) => ({ job, span: intervalOn(job, day)! }))
    .sort((a, b) => a.span.startMinute - b.span.startMinute || a.job.id.localeCompare(b.job.id));

  const found = new Map<string, string[]>();
  const record = (a: string, b: string) => {
    const existing = found.get(a);
    if (existing) {
      if (!existing.includes(b)) {
        existing.push(b);
      }
    } else {
      found.set(a, [b]);
    }
  };

  let active: typeof spans = [];
  for (const entry of spans) {
    active = active.filter((candidate) => candidate.span.endMinute > entry.span.startMinute);
    for (const overlapping of active) {
      record(entry.job.id, overlapping.job.id);
      record(overlapping.job.id, entry.job.id);
    }
    active.push(entry);
  }

  return found;
}
